import { Button, Segmented, Space } from "antd";
import DOMPurify from "dompurify";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { splitQuotedHtml, splitQuotedText } from "./email-body-sanitize.util";
import type { ForwardHeaders } from "./email-body-sanitize.util";

const SANDBOX = "allow-scripts allow-popups allow-popups-to-escape-sandbox";

const PURIFY_OPTIONS = {
  WHOLE_DOCUMENT: false,
  FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "meta", "link"],
  FORBID_ATTR: ["srcset", "ping", "formaction"],
};

const ACCENT = "#6366f1";
const ACCENT_SOFT = "#f5f4ff";
const BORDER = "#e6e6ef";
const MUTED = "#8c8c99";

type ViewMode = "html" | "text";

function escapeHtml(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function splitAddress(raw: string): { name: string; address: string | null } {
  const m = /^\s*(.*?)\s*<([^>]+)>\s*$/.exec(raw);
  if (!m) return { name: raw.trim(), address: null };
  return { name: m[1].replace(/^["']|["']$/g, "").trim() || m[2], address: m[2] };
}

function formatAddress(raw: string): string {
  const { name, address } = splitAddress(raw);
  if (!address || address === name) return escapeHtml(name);
  return `${escapeHtml(name)}<span class="fwd-dim"> [${escapeHtml(address)}]</span>`;
}

export function formatAddressList(raw: string): string {
  return raw
    .split(/,\s*(?![^<]*>)/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map(formatAddress)
    .join(", ");
}

function renderHeaderRows(headers: ForwardHeaders): string {
  const rows: Array<[string, string]> = [];

  if (headers.from) rows.push(["From", formatAddress(headers.from)]);
  if (headers.subject) rows.push(["Subject", escapeHtml(headers.subject)]);
  if (headers.date) rows.push(["Date", escapeHtml(headers.date)]);
  if (headers.to) rows.push(["To", formatAddressList(headers.to)]);
  if (headers.cc) rows.push(["Cc", formatAddressList(headers.cc)]);

  return rows.map(([k, v]) => `<dt>${k}</dt><dd title="${escapeHtml(v.replace(/<[^>]*>/g, ""))}">${v}</dd>`).join("");
}

interface EmailBodyProps {
  title: string;
  html?: string;
  text?: string;
  showImages?: boolean;
  imageProxyOrigin?: string;
}

export function EmailBody({ html, text, title, showImages = true, imageProxyOrigin }: Readonly<EmailBodyProps>) {
  const hasHtml = Boolean(html?.trim());
  const hasText = Boolean(text?.trim());

  const [mode, setMode] = useState<ViewMode>(hasHtml ? "html" : "text");
  const [expanded, setExpanded] = useState(false);
  const [height, setHeight] = useState(80);
  const ref = useRef<HTMLIFrameElement>(null);

  const isTextMode = mode === "text" || !hasHtml;

  const { main, quoted, forwarded } = useMemo(
    () => (isTextMode ? splitQuotedText(text ?? "") : splitQuotedHtml(html ?? "")),
    [isTextMode, html, text],
  );

  const channel = useMemo(() => crypto.randomUUID(), []);

  const srcDoc = useMemo(() => {
    const nonce = crypto.randomUUID();
    const prep = (value: string) => (isTextMode ? escapeHtml(value) : DOMPurify.sanitize(value, PURIFY_OPTIONS));

    const imgSrc = showImages ? `img-src ${imageProxyOrigin ?? "https:"} data: cid:` : `img-src 'none'`;

    const csp = [
      `default-src 'none'`,
      `script-src 'nonce-${nonce}'`,
      imgSrc,
      `style-src 'unsafe-inline'`,
      `font-src data:`,
    ].join("; ");

    const senderLabel = forwarded?.headers.from ? escapeHtml(splitAddress(forwarded.headers.from).name) : null;

    const forwardBlock = forwarded
      ? `<section class="fwd">
  <div class="fwd-hd" id="fwd-hd" role="button" tabindex="0" aria-expanded="true" aria-controls="fwd-body">
    <div class="fwd-main">
      <div class="fwd-cap">
        <span class="fwd-title">Forwarded message</span>
        ${senderLabel ? `<span class="fwd-from">from ${senderLabel}</span>` : ""}
      </div>
      <dl class="fwd-meta" id="fwd-meta">${renderHeaderRows(forwarded.headers)}</dl>
    </div>
    <span class="fwd-chev" id="fwd-chev" aria-hidden="true"></span>
  </div>
  <div class="fwd-body content" id="fwd-body">${prep(forwarded.body)}</div>
</section>`
      : "";

    return `<!doctype html><html><head>
<meta http-equiv="Content-Security-Policy" content="${csp}">
<meta name="viewport" content="width=device-width, initial-scale=1">
<base target="_blank">
<style>
  html, body { margin: 0; padding: 0; }
  body { overflow-x: hidden; }
  #root {
    font-family: system-ui, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    word-break: break-word;
    color: #1f1f28;
  }
  #root::after { content: ""; display: table; clear: both; }
  #root img { max-width: 100%; height: auto; }
  #root table { max-width: 100%; }
  .content { ${isTextMode ? "white-space: pre-wrap;" : ""} }
  #q[hidden], #fwd-body[hidden] { display: none !important; }

    .fwd {
    margin-top: 16px;
    border: 1px solid ${BORDER};
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
  }
  .fwd-hd {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 10px 12px;
    cursor: pointer;
    background: ${ACCENT_SOFT};
    border-left: 3px solid ${ACCENT};
    white-space: normal;
  }
  .fwd-hd:hover { background: #efecff; }
  .fwd-hd:focus-visible { outline: 2px solid ${ACCENT}; outline-offset: -2px; }
  .fwd-main { flex: 1 1 auto; min-width: 0; }
  .fwd-cap { display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap; }
  .fwd-title { font-size: 12px; font-weight: 600; color: ${ACCENT}; letter-spacing: .01em; }
  .fwd-from { font-size: 12px; color: ${MUTED}; }

  .fwd-chev {
    flex: 0 0 auto;
    width: 9px;
    height: 9px;
    margin: 4px 3px 0 0;
    border-right: 1.5px solid ${MUTED};
    border-bottom: 1.5px solid ${MUTED};
    transform: rotate(45deg);
    transition: transform .15s ease;
  }
  .fwd-chev.collapsed { transform: rotate(-45deg); }

  .fwd-meta {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    column-gap: 12px;
    row-gap: 3px;
    margin: 6px 0 0;
    font-size: 12px;
  }
  .fwd-meta[hidden] { display: none !important; }
  .fwd-meta dt { color: ${MUTED}; }
  .fwd-meta dd { margin: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .fwd-dim { color: ${MUTED}; }

  .fwd-body { padding: 12px; }
  .fwd-body > *:first-child { margin-top: 0; }
  .fwd-body > *:last-child { margin-bottom: 0; }
  .fwd-body p:first-child, .fwd-body div:first-child > p:first-child { margin-top: 0; }
</style>
</head><body>
<div id="root"><div id="m" class="content">${prep(main)}</div>${forwardBlock}<div id="q" class="content" hidden>${quoted ? prep(quoted) : ""}</div></div>
<script nonce="${nonce}">
  var root = document.getElementById("root");
  var last = -1;
  function measure() {
    var r = root.getBoundingClientRect().height;
    return Math.ceil(Math.max(r, root.scrollHeight, root.offsetHeight));
  }
  function send(force) {
    var h = measure();
    if (h === last && !force) return;
    last = h;
    parent.postMessage({ ch: "${channel}", t: "h", h: h }, "*");
  }
  new ResizeObserver(function () { send(false); }).observe(root);
  addEventListener("load", function () { send(true); });
  addEventListener("message", function (e) {
    if (e.data && e.data.t === "toggle") {
      document.getElementById("q").hidden = !e.data.open;
      requestAnimationFrame(function () { send(true); });
    }
  });

  var fh = document.getElementById("fwd-hd");
  if (fh) {
    var fb = document.getElementById("fwd-body");
    var fm = document.getElementById("fwd-meta");
    var fc = document.getElementById("fwd-chev");
    var open = true;
    function toggleFwd() {
      open = !open;
      fb.hidden = !open;
      fm.hidden = !open;
      fc.className = "fwd-chev" + (open ? "" : " collapsed");
      fh.setAttribute("aria-expanded", String(open));
      requestAnimationFrame(function () { send(true); });
    }
    fh.addEventListener("click", toggleFwd);
    fh.addEventListener("keydown", function (e) {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleFwd(); }
    });
  }

  send(true);
  [0, 50, 250, 800].forEach(function (d) { setTimeout(function () { send(false); }, d); });
</script>
</body></html>`;
  }, [main, quoted, forwarded, isTextMode, showImages, imageProxyOrigin, channel]);

  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (e.data?.ch !== channel || e.data?.t !== "h") return;
      if (typeof e.data.h === "number" && e.data.h > 0) setHeight(e.data.h);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [channel]);

  const pushQuoteState = useCallback((open: boolean) => {
    ref.current?.contentWindow?.postMessage({ t: "toggle", open }, "*");
  }, []);

  const handleLoad = useCallback(() => {
    pushQuoteState(expanded && Boolean(quoted));
  }, [expanded, quoted, pushQuoteState]);

  const toggleQuote = () => {
    const next = !expanded;
    setExpanded(next);
    pushQuoteState(next);
  };

  return (
    <div>
      <iframe
        title={title}
        ref={ref}
        sandbox={SANDBOX}
        srcDoc={srcDoc}
        onLoad={handleLoad}
        height={height}
        style={{ width: "100%", border: 0, display: "block" }}
      />
      <Space size={8} style={{ marginTop: 4 }}>
        {quoted && (
          <Button
            size="small"
            type="text"
            shape="round"
            onClick={toggleQuote}
            aria-expanded={expanded}
            style={{ background: "#f0f0f5", fontSize: 12 }}
          >
            {expanded ? "Hide quote" : "Show quote"}
          </Button>
        )}
        {hasHtml && hasText && (
          <Segmented
            size="small"
            shape="round"
            value={mode}
            className="lowercase"
            onChange={(v) => setMode(v as ViewMode)}
            options={[
              { label: "html", value: "html" },
              { label: "text", value: "text" },
            ]}
          />
        )}
      </Space>
    </div>
  );
}
